import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { ApiError, api, post } from "../api";
import { Steps } from "../components/Steps";

const errors: Record<string, string> = {
  cancelled: "Log-in was cancelled. Try again when you're ready.",
  expired: "That log-in took too long. Please try again.",
  email_in_use: "This email already has a Brandwall account. Log in the way you did last time.",
  no_email: "That account didn't give us your email address. Try another way to log in.",
  login_failed: "Log-in failed. Please try again.",
};

type Providers = { google: boolean; microsoft: boolean; password: boolean; dev: boolean };

export function Login() {
  const [params] = useSearchParams();
  const returnTo = params.get("returnTo") ?? "/";
  const providers = useQuery({
    queryKey: ["providers"],
    queryFn: () => api<Providers>("/auth/providers"),
  });
  const [mode, setMode] = useState<"signup" | "login">(
    params.get("mode") === "login" ? "login" : "signup",
  );
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(errors[params.get("error") ?? ""] ?? null);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const start = (p: "google" | "microsoft") => {
    window.location.href = `/api/auth/${p}/start?returnTo=${encodeURIComponent(returnTo)}`;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === "signup")
        await post("/auth/signup", { name, email, password, acceptTerms: terms });
      else await post("/auth/login", { email, password });
      await qc.invalidateQueries({ queryKey: ["me"] });
      navigate(returnTo);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong. Please try again.");
      setBusy(false);
    }
  };

  const p = providers.data;
  const signup = mode === "signup";

  return (
    <div className="onb">
      <div className="box">
        <img className="logo" src="/logo.svg" alt="Brandwall" />
        {signup && <Steps at={0} />}
        <div className="card stack">
          <div>
            <h1>{signup ? "Create your account" : "Log in"}</h1>
            <p className="muted">{signup ? "Start free. No card needed." : "Welcome back."}</p>
          </div>
          {error && (
            <div className="error" role="alert">
              {error}
            </div>
          )}
          {p?.google && (
            <button type="button" className="btn block secondary" onClick={() => start("google")}>
              Continue with Google
            </button>
          )}
          {p?.microsoft && (
            <button
              type="button"
              className="btn block secondary"
              onClick={() => start("microsoft")}
            >
              Continue with Microsoft
            </button>
          )}
          {(p?.google || p?.microsoft) && (
            <p className="small muted" style={{ textAlign: "center" }}>
              or use your work email
            </p>
          )}
          <form className="stack" onSubmit={submit}>
            {signup && (
              <label className="field">
                Your name
                <input
                  className="input"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Maja Storm"
                  maxLength={80}
                />
              </label>
            )}
            <label className="field">
              Work email
              <input
                className="input"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="maja@fjellbu.dk"
              />
            </label>
            <label className="field">
              Password
              <input
                className="input"
                type="password"
                required
                minLength={signup ? 10 : undefined}
                autoComplete={signup ? "new-password" : "current-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {signup && <span className="small muted">At least 10 characters.</span>}
            </label>
            {signup && (
              <label className="row small" style={{ alignItems: "flex-start", gap: 8 }}>
                <input
                  type="checkbox"
                  required
                  checked={terms}
                  onChange={(e) => setTerms(e.target.checked)}
                  style={{ marginTop: 3 }}
                />
                <span>
                  I accept the{" "}
                  <a href="/terms" target="_blank" rel="noreferrer">
                    terms
                  </a>{" "}
                  for my business, and I've read the{" "}
                  <a href="/privacy" target="_blank" rel="noreferrer">
                    privacy policy
                  </a>
                  .
                </span>
              </label>
            )}
            <button type="submit" className="btn block" disabled={busy}>
              {busy ? "One moment…" : signup ? "Create account" : "Log in"}
            </button>
          </form>
          <p className="small" style={{ textAlign: "center" }}>
            {signup ? "Already have an account? " : "New to Brandwall? "}
            <button
              type="button"
              className="btn link"
              onClick={() => {
                setMode(signup ? "login" : "signup");
                setError(null);
              }}
            >
              {signup ? "Log in" : "Create an account"}
            </button>
          </p>
        </div>
        <p className="small muted" style={{ textAlign: "center" }}>
          {signup ? "By creating an account you accept our " : "Read our "}
          <a href="/terms">terms</a> and <a href="/privacy">privacy policy</a>.
        </p>
      </div>
    </div>
  );
}
